import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export function Navbar() {
  const { lang, setLang, t, languages } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#savoir-faire", t("nav_savoir")],
    ["#creations", t("nav_produits")],
    ["#sculpture", t("nav_sculpture")],
    ["#realisations", t("nav_realisations")],
    ["#journal", t("nav_blog")],
    ["#contact", t("nav_contact")],
  ];

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#0B0A08]/85 backdrop-blur-xl border-b hairline" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <a href="#top" data-testid="nav-brand-logo" className="flex flex-col leading-none group">
          <span className="font-serif-d text-xl md:text-2xl tracking-[0.18em] text-[#F7F4EE]">
            ARCHWOOD <span className="gold-text">FRANCE</span>
          </span>
          <span className="text-[9px] font-mono-d tracking-[0.4em] text-[#7A7367] mt-1 group-hover:text-[#C5A059] transition-colors duration-300">
            MENUISERIE D'ART · PARIS
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-8">
          {links.map(([href, label]) => (
            <a key={href} href={href} data-testid={`nav-link-${href.slice(1)}`}
              className="text-[13px] tracking-[0.12em] uppercase text-[#B8B0A2] hover:text-[#D4AF37] transition-colors duration-300">
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="language-selector-dropdown"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 border hairline text-xs font-mono-d uppercase tracking-widest text-[#B8B0A2] hover:text-[#D4AF37] hover:border-[rgba(212,175,55,0.6)] transition-colors duration-300"
            >
              <Globe size={14} />
              {lang}
              <ChevronDown size={12} className={`transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  data-testid="language-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25 }}
                  className="absolute right-0 mt-2 w-44 max-h-80 overflow-auto bg-[#14120F] border hairline shadow-2xl shadow-black/60 z-50"
                >
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${l.code === lang ? "text-[#D4AF37] bg-[#1C1914]" : "text-[#B8B0A2] hover:text-[#F7F4EE] hover:bg-[#1C1914]"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a href="#expression" data-testid="nav-cta-expression"
            className="gold-btn hidden md:inline-flex items-center border border-[#D4AF37] px-5 py-2.5 text-[12px] tracking-[0.15em] uppercase text-[#D4AF37]">
            {t("nav_cta")}
          </a>

          <button data-testid="mobile-menu-button" onClick={() => setMobileOpen((v) => !v)} className="xl:hidden text-[#F7F4EE] p-2">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-[#0B0A08]/95 backdrop-blur-xl border-b hairline overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map(([href, label]) => (
                <a key={href} href={href} onClick={() => setMobileOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-[#B8B0A2] hover:text-[#D4AF37]">
                  {label}
                </a>
              ))}
              <a href="#expression" onClick={() => setMobileOpen(false)} className="text-sm tracking-[0.15em] uppercase text-[#D4AF37]">
                {t("nav_cta")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
